import { useQuery } from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { ConnectorInvocationStatus } from '@/types/core/connector-invocation-status';
import type { UUID } from '@/utils/uuid';

import { connectorQueryKeys } from './query-keys';

/**
 * Hook to find a specific connector feature invocation status by run sequence.
 * Polling is driven by useConnectorFeatureRunStatus via usePollingRefetch.
 */
export function useFindConnectorFeatureInvocationStatus(
  tenantId?: UUID,
  connectorId?: UUID,
  featureId?: UUID,
  runSequence?: number
) {
  return useQuery<ConnectorInvocationStatus | null, Error>({
    queryKey: connectorQueryKeys.invocationStatus(
      tenantId,
      connectorId,
      featureId,
      runSequence
    ),
    queryFn: async () => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!connectorId) throw new Error('Missing connector id');
      if (!featureId) throw new Error('Missing feature id');
      if (runSequence === undefined) throw new Error('Missing run sequence');
      return connectorService.findConnectorInvocationStatus(
        tenantId,
        connectorId,
        featureId,
        runSequence
      );
    },
    placeholderData: (prev) => prev,
    enabled:
      !!tenantId && !!connectorId && !!featureId && runSequence !== undefined,
  });
}
